import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
  signal,
  inject,
} from '@angular/core';
import {
  IActivo,
  IEmergencia,
  IEventoGenerico,
  IFilter,
  IPersonalSalud,
  IQueryParam,
  IReporteGenerico,
  IUsuario,
  IValoresEventoEmergencia,
} from 'modelos/src';
import mapboxgl from 'mapbox-gl';
import { RouterLink } from '@angular/router';
import { NgTemplateOutlet } from '@angular/common';
import { Subscription } from 'rxjs';
import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { EmergenciasService } from '../../../../../auxiliares/servicios/http/emergencias.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import {
  MapCardComponent,
  MapCardConfig,
} from '../../../../../componentes/map-card/map-card.component';
import { PIEZAS_DETALLE } from '../../../../../componentes/detalle/detalle.components';
import {
  MapaFacilData,
  MarkerMapa,
} from '../../../../../auxiliares/mapa-facil/interfaces';
import {
  fechaHoraEmergencia as fechaHora,
  formatDuracion as duracion,
} from '../emergencia.service';
import {
  IPresentacion,
  estaFinalizada,
  presentacionPrioridad,
} from '../../estado-emergencia';

/**
 * Una fila de una tarjeta: texto, o una cápsula con el color de la paleta de
 * estados y códigos (estado-emergencia.ts), o un link.
 */
interface Fila {
  etiqueta: string;
  valor?: string | null;
  badges?: IPresentacion[];
  ruta?: string[];
}

interface Tarjeta {
  titulo: string;
  icono: string;
  filas: Fila[];
}

/** El resumen de arriba: los tiempos (estado y código van en el encabezado). */
interface Resumen {
  respuesta: string;
  reasignaciones: string;
  tituloTotal: string;
  total: string;
}

const SEXO: Record<string, string> = {
  M: 'Masculino',
  F: 'Femenino',
  X: 'No binario',
};

@Component({
  selector: 'app-detalles-emergencias-info',
  templateUrl: './detalles-emergencias-info.component.html',
  styleUrl: './detalles-emergencias-info.component.scss',
  standalone: true,
  imports: [
    AuxiliaresModule,
    MapCardComponent,
    NgTemplateOutlet,
    RouterLink,
    ...PIEZAS_DETALLE,
  ],
})
export class DetallesEmergenciasInfoComponent implements OnChanges, OnDestroy {
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private emergenciasService = inject(EmergenciasService);

  @Input() public emergencia?: IEmergencia;

  @ViewChild(MapCardComponent) private mapCardRef?: MapCardComponent;

  /**
   * El mapa llena el alto de la columna de las tarjetas (como en el detalle
   * de tracker): se mide en px, porque map-card le resta su encabezado al alto
   * que recibe. Con un alto fijo sobraba espacio abajo del mapa.
   */
  public pantallaAncha = signal(window.innerWidth > 1000);
  public altoMapa = signal<number | null>(null);
  private observadorMapa?: ResizeObserver;

  @ViewChild('contenedorMapa') set contenedorMapa(
    ref: ElementRef<HTMLElement> | undefined,
  ) {
    this.observadorMapa?.disconnect();
    if (!ref) return;
    this.observadorMapa = new ResizeObserver(([entrada]) => {
      this.altoMapa.set(Math.round(entrada.contentRect.height) || null);
    });
    this.observadorMapa.observe(ref.nativeElement);
  }

  @HostListener('window:resize')
  public alRedimensionar(): void {
    this.pantallaAncha.set(window.innerWidth > 1000);
  }

  public resumen?: Resumen;
  /** Datos de la emergencia, paciente y asignación (al lado del mapa). */
  public tarjetas: Tarjeta[] = [];
  /** Datos médicos y tiempos (a lo ancho). */
  public tarjetasAnchas: Tarjeta[] = [];
  public mapCardConfig?: MapCardConfig;
  public mapOptions?: MapaFacilData;

  /** Último evento con sus `detallesEmergencias` populados. */
  private evento?: IEventoGenerico;
  private idEventoCargado?: string;

  // Seguimiento en vivo del móvil asignado (sólo con la emergencia activa)
  private idMovilSeguido?: string;
  private movilCoords?: [number, number];
  private ultimoReporteMovil?: IReporteGenerico;
  private reporte$?: Subscription;
  private encuadrado = false;

  // Carga

  private async cargarEvento(): Promise<void> {
    const id = this.emergencia?.ultimoEventoEmergencia?._id;
    if (!id) {
      this.evento = this.emergencia?.ultimoEventoEmergencia;
      this.idEventoCargado = undefined;
      return;
    }
    if (id === this.idEventoCargado) return;
    try {
      // El primero del seguimiento es el último evento, ya populado y sin
      // datos del cliente (GET /emergencias/:id/seguimiento).
      const eventos = await this.emergenciasService.seguimiento(
        this.emergencia!._id!,
      );
      this.evento = eventos?.[0] ?? this.emergencia?.ultimoEventoEmergencia;
      this.idEventoCargado = id;
    } catch (error) {
      console.error('No se pudo traer el último evento', error);
      this.evento = this.emergencia?.ultimoEventoEmergencia;
    }
  }

  // Seguimiento del móvil

  private seguirMovil(): void {
    const idMovil = this.evento?.detallesEmergencias?.idMovilAsignado;
    const activa = !estaFinalizada(this.emergencia);
    if (!activa || !idMovil) {
      this.dejarDeSeguir();
      return;
    }
    if (idMovil === this.idMovilSeguido) return;
    this.dejarDeSeguir();
    this.idMovilSeguido = idMovil;

    const filter: IFilter<IReporteGenerico> = {
      idsAsignados: { $in: [idMovil] },
    } as IFilter<IReporteGenerico>;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: '-fechaCreacion',
      limit: 1,
    };
    this.reporte$ = this.lista2
      .ultimosReportesGenericos<'Tracker 4G'>(query, 15)
      .subscribe((data) => {
        const reporte = data?.datos?.[0];
        if (!reporte) return;
        this.ultimoReporteMovil = reporte as IReporteGenerico;
        const coords = reporte.valores?.geojson?.coordinates as
          | [number, number]
          | undefined;
        if (coords?.[0] && coords?.[1]) this.movilCoords = coords;
        this.armarMapa();
        this.armarTablas();
        this.encuadrar();
      });
  }

  private dejarDeSeguir(): void {
    this.reporte$?.unsubscribe();
    this.reporte$ = undefined;
    this.idMovilSeguido = undefined;
    this.movilCoords = undefined;
    this.ultimoReporteMovil = undefined;
  }

  // Resumen

  private armarResumen(): void {
    const e = this.emergencia;
    const creacion = this.ms(e?.fechaCreacion);
    const asignacion = this.ms(e?.fechaPrimeraAsignacion);
    const fin = this.ms(e?.fechaFinalizacion);
    const finalizada = estaFinalizada(e);
    this.resumen = {
      reasignaciones: (e?.cantidadReasignaciones ?? 0).toLocaleString('es-AR'),
      respuesta:
        creacion && asignacion
          ? duracion(asignacion - creacion)
          : 'Sin asignar',
      tituloTotal: finalizada ? 'Tiempo total' : 'Tiempo transcurrido',
      total: creacion
        ? duracion((finalizada && fin ? fin : Date.now()) - creacion)
        : 'Sin información',
    };
  }

  // Mapa

  private armarMapa(): void {
    const e = this.emergencia;
    const coords = e?.ubicacionDestino?.coordinates as
      | [number, number]
      | undefined;
    if (!coords?.[0] || !coords?.[1]) {
      this.mapCardConfig = undefined;
      this.mapOptions = undefined;
      return;
    }

    this.mapCardConfig = {
      title: 'Destino de la emergencia',
      subtitle: e?.direccion || 'Sin dirección',
      icon: 'place',
      color: 'red',
      nombreMap: 'MapCardEmergenciaMedica',
    };

    const markers: MarkerMapa[] = [
      {
        label: 'emergenciaMedica',
        coor: coords,
        centrar: false,
        id: e?._id,
        data: {
          _id: e?._id,
          ubicacionDestino: e?.ubicacionDestino,
          ultimoEventoEmergencia: e?.ultimoEventoEmergencia,
          prioridadApertura: e?.prioridadApertura,
          prioridadCierre: e?.prioridadCierre,
          codigo: e?.codigo,
          direccion: e?.direccion,
        },
      },
    ];
    if (this.movilCoords) {
      markers.push({
        label: 'ambulancia',
        coor: this.movilCoords,
        centrar: false,
        data: { asignada: true },
      });
    }
    const hospital = this.evento?.detallesEmergencias?.hospital;
    const coordsHospital = hospital?.geojson?.coordinates as
      | [number, number]
      | undefined;
    if (!estaFinalizada(e) && coordsHospital?.[0] && coordsHospital?.[1]) {
      markers.push({
        label: 'hospital',
        coor: coordsHospital,
        centrar: false,
        id: hospital?._id,
        data: hospital,
      });
    }

    this.mapOptions = {
      markers,
      // Centro y zoom hasta que el mapa existe: después manda el usuario. Antes
      // se mandaban sólo la primera vez que se armaba, pero se arma dos veces
      // antes de que el mapa esté (antes y después de traer el evento) y el
      // centro se perdía: el mapa quedaba en la vista por defecto.
      ...(this.mapCardRef?.getMap() ? {} : { center: coords, zoom: 16 }),
    };
  }

  /** Encuadra destino + móvil + hospital la primera vez que hay más de uno. */
  private encuadrar(): void {
    if (this.encuadrado) return;
    const puntos = (this.mapOptions?.markers || []).map((m) => m.coor);
    if (puntos.length < 2) return;
    this.encuadrado = true;
    setTimeout(() => {
      const map = this.mapCardRef?.getMap();
      if (!map) return;
      const bounds = new mapboxgl.LngLatBounds();
      puntos.forEach((p) => bounds.extend(p as mapboxgl.LngLatLike));
      map.fitBounds(bounds, { padding: 60, maxZoom: 16 });
    }, 1000);
  }

  // Tablas

  private armarTablas(): void {
    const e = this.emergencia;
    if (!e) {
      this.tarjetas = [];
      this.tarjetasAnchas = [];
      return;
    }
    const det = this.evento?.detallesEmergencias;
    const em = e.emergenciaMedica;
    const paciente = e.destinatarioAsistencia;

    const datos: Tarjeta = {
      titulo: 'Emergencia',
      icono: 'emergency',
      filas: [
        { etiqueta: 'Número', valor: e.codigo != null ? `#${e.codigo}` : null },
        {
          etiqueta: 'Código de apertura',
          badges: e.prioridadApertura
            ? [presentacionPrioridad(e.prioridadApertura)]
            : undefined,
        },
        {
          etiqueta: 'Código de cierre',
          badges: e.prioridadCierre
            ? [presentacionPrioridad(e.prioridadCierre)]
            : undefined,
        },
        { etiqueta: 'Solicitante', valor: e.solicitante },
        { etiqueta: 'Teléfono', valor: e.telefono },
        { etiqueta: 'Dirección', valor: e.direccion },
        { etiqueta: 'Cliente', valor: e.cliente?.nombre },
        { etiqueta: 'Fecha de creación', valor: fechaHora(e.fechaCreacion) },
        { etiqueta: 'Observaciones', valor: e.observaciones },
      ],
    };

    const nombrePaciente = [paciente?.nombre, paciente?.apellido]
      .filter(Boolean)
      .join(' ');
    const pacienteTarjeta: Tarjeta = {
      titulo: 'Paciente',
      icono: 'personal_injury',
      filas: [
        {
          etiqueta: 'Nombre',
          valor: nombrePaciente || null,
          ruta:
            paciente?._id && nombrePaciente
              ? [
                  '/main/emergencias/destinatariosAsistencia/detalles',
                  paciente._id,
                ]
              : undefined,
        },
        { etiqueta: 'DNI', valor: paciente?.dni },
        {
          etiqueta: 'Edad',
          valor: paciente?.edad != null ? `${paciente.edad} años` : null,
        },
        {
          etiqueta: 'Sexo',
          valor: paciente?.sexo ? SEXO[paciente.sexo] || paciente.sexo : null,
        },
        { etiqueta: 'Obra social', valor: paciente?.obraSocial },
        { etiqueta: 'Teléfono', valor: paciente?.telefono },
        {
          etiqueta: 'Teléfono alternativo',
          valor: paciente?.telefonoAlternativo,
        },
      ],
    };

    const movil = det?.movilAsignado as IActivo | undefined;
    const reporte = this.ultimoReporteMovil;
    const velocidad = (reporte?.valores as { velocidad?: number } | undefined)
      ?.velocidad;
    const asignacion: Tarjeta = {
      titulo: 'Asignación',
      icono: 'assignment_ind',
      filas: Object.entries({
        Móvil: movil?.identificacion || null,
        Patente: movil?.vehiculo?.patente || null,
        Chofer: this.nombreUsuario(det?.chofer),
        Médicos: this.nombres(det?.medicos),
        Enfermeros: this.nombres(det?.enfermeros),
        Hospital: det?.hospital?.identificacion || null,
        'Base de operaciones': det?.centroDeAtencion?.identificacion || null,
        Responsable: this.nombreUsuario(det?.usuarioResponsable),
        ...(reporte
          ? {
              'Último reporte del móvil': fechaHora(reporte.fechaCreacion),
              Velocidad:
                velocidad != null
                  ? `${velocidad.toLocaleString('es-AR')} km/h`
                  : null,
            }
          : {}),
      }).map(([etiqueta, valor]) => ({
        etiqueta,
        valor: valor as string | null,
      })),
    };

    const sv = em?.signosVitales;
    const signos = sv
      ? [
          this.medida('T.A.', sv.tensionArterial, 'mmHg'),
          this.medida('F.R.', sv.frecuenciaRespiratoria, 'irpm'),
          this.medida('F.C.', sv.frecuenciaCardiaca, 'lpm'),
          this.medida('Sat. O₂', sv.saturacionOxigeno, '%'),
          this.medida('Temp.', sv.temperatura, '°C'),
        ]
          .filter(Boolean)
          .join(' · ')
      : '';
    const traslado = em?.traslado;
    let textoTraslado: string | null = null;
    if (traslado?.traslado === true) {
      textoTraslado = 'Sí';
      if (traslado.direccion) textoTraslado += `, a ${traslado.direccion}`;
      if (traslado.aceptaTraslado === false) {
        textoTraslado += ' (el paciente no lo acepta)';
      }
    } else if (traslado?.traslado === false) {
      textoTraslado = 'No';
    }
    const valores = this.evento?.valores as IValoresEventoEmergencia;
    const medicos: Tarjeta = {
      titulo: 'Datos médicos',
      icono: 'medical_information',
      filas: Object.entries({
        'Síntomas de apertura': em?.sintomasApertura?.length
          ? em.sintomasApertura.join(', ')
          : null,
        'Síntomas de cierre': em?.sintomasCierre?.length
          ? em.sintomasCierre.join(', ')
          : null,
        Diagnóstico: em?.diagnostico || valores?.diagnostico || null,
        Tratamiento: em?.tratamiento || null,
        Medicación: em?.medicacion || null,
        'Signos vitales': signos || null,
        Traslado: textoTraslado,
      }).map(([etiqueta, valor]) => ({
        etiqueta,
        valor: valor as string | null,
      })),
    };

    const tiempos: Tarjeta = {
      titulo: 'Tiempos',
      icono: 'schedule',
      filas: Object.entries({
        Creación: fechaHora(e.fechaCreacion) || null,
        'Primera asignación': fechaHora(e.fechaPrimeraAsignacion) || null,
        Salida: fechaHora(e.fechaSalida) || null,
        'Llegada a destino': fechaHora(e.fechaLlegadaDestino) || null,
        'Llegada a hospital': fechaHora(em?.fechaLlegadaHospital) || null,
        Finalización: fechaHora(e.fechaFinalizacion) || null,
      }).map(([etiqueta, valor]) => ({
        etiqueta,
        valor: valor as string | null,
      })),
    };

    this.tarjetas = [datos, pacienteTarjeta, asignacion];
    this.tarjetasAnchas = [medicos, tiempos];
  }

  // Auxiliares

  private ms(fecha?: string): number | undefined {
    if (!fecha) return undefined;
    const t = new Date(fecha).getTime();
    return Number.isNaN(t) ? undefined : t;
  }

  private nombreUsuario(u?: IUsuario): string | null {
    if (!u) return null;
    const dp = u.datosPersonales;
    const nombre = dp?.nombre;
    return nombre || u.usuario || null;
  }

  private nombres(personal?: IPersonalSalud[]): string | null {
    const lista = (personal || []).map((p) => p?.nombre).filter(Boolean);
    return lista.length ? lista.join(', ') : null;
  }

  private medida(etiqueta: string, valor?: number, unidad?: string): string {
    if (valor == null) return '';
    return `${etiqueta} ${valor.toLocaleString('es-AR')} ${unidad}`;
  }

  // Hooks

  async ngOnChanges(): Promise<void> {
    if (!this.emergencia) return;
    // Primero lo que no depende del evento populado, para no dejar la
    // solapa vacía mientras llega
    this.armarResumen();
    this.armarMapa();
    this.armarTablas();
    await this.cargarEvento();
    this.armarMapa();
    this.armarTablas();
    this.seguirMovil();
    this.encuadrar();
  }

  ngOnDestroy(): void {
    this.dejarDeSeguir();
    this.observadorMapa?.disconnect();
  }
}
